import { lazy, SyntheticEvent, useState } from 'react';
import {
	ArrayInput,
	AutocompleteInput,
	CheckboxGroupInput,
	FileField,
	FileInput,
	ImageField,
	ImageInput,
	NumberInput,
	ReferenceArrayInput,
	ReferenceInput,
	required,
	SelectArrayInput,
	SelectInput,
	SimpleFormIterator,
	TextInput,
	useTranslate
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
	Chip,
	Divider,
	InputAdornment,
	Stack,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import {
	contactInputText,
	contactOptionText
} from '../../../../misc/ContactOption';
import ImageEditorField from '../../../../misc/ImageEditorField';
import { ImagesInput } from '../../../../misc/ImagesInput';
import { TabContent } from '../../../../misc/TabContent';
import {
	FastCreateBuilding,
	FastCreateComplex,
	PriceByAreaInputs
} from '../../../components';
import {
	INPUT_SOURCE,
	OBJECTS_TYPES,
	SALE_RENT_TYPE
} from '../../../constants';
import { useApartmentStates } from '../../../hooks';

import 'react-image-gallery/styles/css/image-gallery.css';

const PlacementSectionInputs = () => {
	const { apartmentStates } = useApartmentStates();

	return (
		<Stack gap={1}>
			<Stack gap={2} mb={2} direction="row" alignItems="center">
				<TextInput
					source={INPUT_SOURCE.NAME}
					helperText={false}
					label="form.fields.name"
				/>
			</Stack>
			<Stack gap={2} direction="row" alignItems="center">
				<ReferenceInput source={INPUT_SOURCE.COMPLEX_ID} reference="complexes">
					<AutocompleteInput
						optionText="name"
						blurOnSelect="mouse"
						create={<FastCreateComplex />}
						filterToQuery={searchText => ({ name: searchText })}
						label="form.fields.complex"
					/>
				</ReferenceInput>
				<ReferenceInput source={INPUT_SOURCE.BUILDING_ID} reference="buildings">
					<AutocompleteInput
						optionText="name"
						label="form.fields.building"
						blurOnSelect="mouse"
						create={<FastCreateBuilding />}
						filterToQuery={searchText => ({ name: searchText })}
						validate={required()}
					/>
				</ReferenceInput>
				<SelectInput
					source={INPUT_SOURCE.STATE}
					label="form.fields.state"
					choices={apartmentStates as { id: number; name: string }[]}
					validate={required()}
				/>
			</Stack>
			<Stack gap={2} direction="row" alignItems="center">
				<NumberInput
					source={INPUT_SOURCE.AREA}
					label="form.fields.area"
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="start">м²</InputAdornment>
						}
					}}
					validate={required()}
				/>
				<NumberInput
					source={INPUT_SOURCE.FLOOR}
					label="form.fields.floor"
					validate={required()}
				/>
				<NumberInput
					source={INPUT_SOURCE.APARTMENT_NUMBER}
					label="form.fields.apartmentNumber"
				/>
				<NumberInput
					source={INPUT_SOURCE.NUMBER_OF_ROOMS}
					label="form.fields.numberOfRooms"
				/>
			</Stack>
			<RichTextInput
				source="description"
				helperText={false}
				label="form.fields.description"
				fullWidth={true}
			/>
		</Stack>
	);
};

const RichTextInput = lazy(() =>
	import('ra-input-rich-text').then(module => ({
		default: module.RichTextInput
	}))
);

const DescriptionSectionInputs = () => {
	const { watch } = useFormContext();
	const objectActions: string[] = watch(INPUT_SOURCE.OBJECT_ACTIONS);
	const translate = useTranslate();

	return (
		<Stack gap={1}>
			<CheckboxGroupInput
				source={INPUT_SOURCE.OBJECT_ACTIONS}
				label="form.fields.objectActions"
				helperText={false}
				choices={[
					{ id: SALE_RENT_TYPE.RENT, name: 'form.fields.rent' },
					{ id: SALE_RENT_TYPE.SALE, name: 'form.fields.sale' }
				]}
			/>
			{objectActions?.map(action => (
				<PriceByAreaInputs
					key={action}
					title={translate(`form.fields.${action}`)}
					priceType={action}
				/>
			))}
		</Stack>
	);
};

const DocumentsSectionInputs = () => {
	return (
		<Stack gap={2}>
			<FileInput
				label="form.fields.documents"
				multiple={true}
				source={INPUT_SOURCE.DOCUMENTS}
				placeholder={
					<p>Натисніть або перетягніть кілька документів, щоб завантажити</p>
				}
				helperText={false}
				accept={{
					'application/pdf': [],
					'text/plain': [],
					'text/csv': [],
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
						[] // .docx
				}}
			>
				<FileField source="src" title="title" />
			</FileInput>
		</Stack>
	);
};

const ContactsSectionInputs = () => {
	return (
		<>
			<ArrayInput source="contacts_jsonb" label="Контакти" helperText={false}>
				<SimpleFormIterator inline disableReordering>
					<ReferenceArrayInput
						source="contact_ids"
						reference="contacts_summary"
					>
						<AutocompleteInput
							label="Контакт"
							optionText={contactOptionText}
							inputText={contactInputText}
							helperText={false}
							validate={required()}
							onCreate={() => {}}
						/>
					</ReferenceArrayInput>
					<ReferenceArrayInput
						resource="objects"
						source="tags"
						reference="tags"
					>
						<SelectArrayInput
							label="Ролі"
							optionText={({ name, color }) => (
								<Chip
									label={name}
									size="small"
									style={{
										backgroundColor: color,
										border: 0
									}}
								/>
							)}
							helperText={false}
							onCreate={() => {}}
							sx={{
								width: 200,
								minWidth: 200,
								'& .RaSelectArrayInput-chip': {
									backgroundColor: 'transparent'
								}
							}}
						/>
					</ReferenceArrayInput>
				</SimpleFormIterator>
			</ArrayInput>
			<TextInput
				source="comments"
				label="Коментарі"
				helperText={false}
				multiline={true}
			/>
			<Stack>
				<Typography variant="h6">Акаунт менеджер</Typography>
				<ReferenceInput
					source="sales_id"
					reference="sales"
					filter={{
						'disabled@neq': true
					}}
				>
					<SelectInput
						label="Акаунт менеджер"
						helperText={false}
						optionText={choice => `${choice.first_name} ${choice.last_name}`}
						validate={required()}
					/>
				</ReferenceInput>
			</Stack>
		</>
	);
};

export const ApartmentInputs = () => {
	const theme = useTheme();
	const [tabIndex, setTabIndex] = useState(0);
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const translate = useTranslate();

	const handleTabChange = (event: SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	return (
		<Stack>
			<Stack p={2}>
				<ImageEditorField
					width={72}
					height={48}
					source="avatar"
					linkPosition="right"
					defaultImageSrc="/img/download.png"
				/>
				<ImagesInput inputSource={INPUT_SOURCE.IMAGES} />
				<FileInput
					label="form.fields.videos"
					multiple={true}
					source="video"
					placeholder={
						<p>Натисніть або перетягніть кілька відео, щоб завантажити</p>
					}
					helperText={false}
					accept={{
						'video/*': []
					}}
				>
					<FileField source="src" title="title" />
				</FileInput>
				<ImageInput
					source="layout"
					label="form.fields.layout"
					placeholder={<p>Натисніть або перетягніть фото, щоб завантажити</p>}
					helperText={false}
				>
					<ImageField source="src" title="title" />
				</ImageInput>
			</Stack>
			<Tabs
				value={tabIndex}
				onChange={handleTabChange}
				textColor="inherit"
				variant="fullWidth"
			>
				<Tab label="Інформація" />
				<Tab label="Технічні деталі" />
				<Tab label="Ціни" />
				<Tab label="Документи" />
				<Tab label="Контакти" />
			</Tabs>
			<Divider />
			<Stack gap={2} p={3}>
				<TabContent value={tabIndex} index={0}>
					<PlacementSectionInputs />
				</TabContent>
				<TabContent value={tabIndex} index={1}>
					{/*<DescriptionSectionInputs />*/}
				</TabContent>
				<TabContent value={tabIndex} index={2}>
					<DescriptionSectionInputs />
				</TabContent>
				<TabContent value={tabIndex} index={3}>
					<DocumentsSectionInputs />
				</TabContent>
				<TabContent value={tabIndex} index={4}>
					{/*<ContactsSectionInputs />*/}
				</TabContent>
			</Stack>
		</Stack>
	);
};
